class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');

    /* Reported live: on some devices you could still scroll the page
       behind the drawer down far enough to see space appear below the
       checkout button, even with the overflow-hidden class above.
       overflow:hidden on body/html blocks scrolling on desktop, but is
       well known to NOT fully block touch-driven scrolling on iOS
       Safari in particular (the page can still rubber-band/scroll a
       little behind a "locked" modal). The bulletproof fix for that is
       to also pin body with position:fixed at its current scroll
       offset while the drawer is open - a position:fixed element has
       nothing to scroll, on any browser or input method, no exceptions.
       Save the scroll position first and restore it on close so the
       page doesn't jump. */
    this.isaScrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + this.isaScrollLockY + 'px';
    document.body.style.width = '100%';

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('overflow-hidden');

    /* Undo the position:fixed scroll-lock from open() and restore the
       exact scroll position the page was at before the drawer opened. */
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    if (typeof this.isaScrollLockY === 'number') {
      window.scrollTo(0, this.isaScrollLockY);
      this.isaScrollLockY = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /* ISADORA: reported - changing an item's quantity in the drawer made
     the footer (discounts row + checkout button) visibly disappear
     for about a second and then reappear already updated, even though
     a quantity change never actually changes the footer's STRUCTURE -
     only numbers it displays.
     Root cause: the stock Dawn selector here was '.drawer__inner',
     which is the single wrapper around literally the entire drawer -
     header, item list AND footer together. Every +/- click therefore
     tore down and recreated the whole subtree from scratch (brand new
     DOM nodes for the footer, checkout button, discount badge, etc.),
     which is what produced the visible blank-then-reappear flash -
     the same class of bug already fixed for add-to-cart's double
     full-render (see cart.js/isadora-cart.js's renderContents /
     onCartUpdate-skip comments).
     Fix: narrow this to just 'cart-drawer-items' (the item rows -
     this part genuinely needs new HTML from the server: fresh
     quantities, line totals, added/removed rows). The footer is
     deliberately left out of this list entirely - isadora-cart.js's
     own refresh() cycle (triggered right after by the MutationObserver
     watching cart-drawer-items) already recalculates and updates
     every dynamic footer value in place via plain textContent/
     innerHTML writes on existing elements (item count, tier discount
     badge + amount, grand total, checkout button price) - no footer
     DOM teardown needed at all, so nothing there ever has a reason to
     flash. */
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
